import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';

interface DealParty {
  name: string;
  type: 'brand' | 'talent' | 'venture';
  details?: string;
}

interface ProfessionalDealOverviewProps {
  deal: any;
  primaryParty: DealParty;
  secondaryParty: DealParty;
  dealType: string;
  children?: React.ReactNode;
}

export const ProfessionalDealOverview: React.FC<ProfessionalDealOverviewProps> = ({
  deal,
  primaryParty,
  secondaryParty,
  dealType,
  children
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMMM dd, yyyy');
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'completed':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'cancelled':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-background">
      {/* Document Header */}
      <div className="text-center mb-8 border-b pb-6">
        <h1 className="text-3xl font-bold tracking-tight mb-4 text-foreground">
          HEADS OF TERMS
        </h1>
        <div className="text-lg text-muted-foreground">
          <div className="font-semibold mb-2">BETWEEN:</div>
          <div className="space-y-1">
            <div className="font-medium">{primaryParty.name.toUpperCase()}</div>
            {primaryParty.details && (
              <div className="text-sm">({primaryParty.details})</div>
            )}
            <div className="my-2 text-base font-semibold">AND</div>
            <div className="font-medium">{secondaryParty.name.toUpperCase()}</div>
            {secondaryParty.details && (
              <div className="text-sm">({secondaryParty.details})</div>
            )}
          </div>
        </div>
      </div>

      {/* Document Content */}
      <div className="space-y-8">
        {/* Deal Status and Overview */}
        <Card className="border-l-4 border-l-primary">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold mb-2">{deal.title}</h2>
                <div className="text-muted-foreground">
                  Deal Reference: #{deal.id.slice(0, 8).toUpperCase()}
                </div>
              </div>
              <Badge className={getStatusColor(deal.status)}>
                {deal.status?.toUpperCase()}
              </Badge>
            </div>
            {deal.description && (
              <p className="text-foreground leading-relaxed">{deal.description}</p>
            )}
          </CardContent>
        </Card>

        {/* Core Terms */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-bold mb-4 text-foreground">
              1. CORE TERMS
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <div className="font-semibold text-foreground mb-1">Designation:</div>
                  <div className="text-muted-foreground capitalize">{dealType} Agreement</div>
                </div>
                <div>
                  <div className="font-semibold text-foreground mb-1">Deal Value:</div>
                  <div className="text-xl font-bold text-foreground">
                    {formatCurrency(deal.deal_value || 0)}
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-foreground mb-1">Deal Type:</div>
                  <div className="text-muted-foreground capitalize">{deal.deal_type || dealType}</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="font-semibold text-foreground mb-1">Agreement Date:</div>
                  <div className="text-muted-foreground">{formatDate(deal.created_at)}</div>
                </div>
                {deal.estimated_completion_date && (
                  <div>
                    <div className="font-semibold text-foreground mb-1">Target Completion:</div>
                    <div className="text-muted-foreground">{formatDate(deal.estimated_completion_date)}</div>
                  </div>
                )}
                <div>
                  <div className="font-semibold text-foreground mb-1">Priority Level:</div>
                  <div className="text-muted-foreground capitalize">{deal.priority || 'Standard'}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Terms */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-bold mb-4 text-foreground">
              2. FINANCIAL TERMS
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="font-semibold text-foreground mb-1">Total Value</div>
                  <div className="text-2xl font-bold text-foreground">
                    {formatCurrency(deal.deal_value || 0)}
                  </div>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="font-semibold text-foreground mb-1">Payment Structure</div>
                  <div className="text-muted-foreground">
                    {deal.payment_terms || 'To be determined'}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Terms */}
        {children && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-bold mb-4 text-foreground">
                3. ADDITIONAL TERMS & CONDITIONS
              </h3>
              {children}
            </CardContent>
          </Card>
        )}

        {/* Document Footer */}
        <div className="text-center pt-6 border-t text-sm text-muted-foreground">
          <div>This document represents the heads of terms for the proposed agreement.</div>
          <div className="mt-2">
            Generated on {formatDate(new Date().toISOString())} • Document ID: {deal.id.slice(0, 8).toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
};